import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  getAnalyticsSummary,
  getActivityAnalytics,
  getRevenueAnalytics,
  getLLMUsage,
  getLLMProviders,
  getContentStats,
} from '../services/adminApiClient';
import adminApiClient from '../services/adminApiClient';
import type {
  AnalyticsSummary,
  ActivityAnalytics,
  RevenueAnalytics,
  LLMUsage,
  LLMProvider,
  ContentStats,
  UserListItem,
} from '../types';

// ---- Helpers ----

function Spinner() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function KpiCard({ label, value, sub, color = 'indigo' }: { label: string; value: ReactNode; sub?: string; color?: string }) {
  const ring: Record<string, string> = {
    indigo: 'border-indigo-200 bg-indigo-50',
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50',
    violet: 'border-violet-200 bg-violet-50',
    slate: 'border-slate-200 bg-slate-50',
  };
  const text: Record<string, string> = {
    indigo: 'text-indigo-700',
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    violet: 'text-violet-700',
    slate: 'text-slate-700',
  };
  return (
    <div className={`border rounded-xl p-4 ${ring[color] ?? ring.indigo}`}>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-bold ${text[color] ?? text.indigo}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionCard({ title, linkTo, linkLabel, children, className = '' }: { title: string; linkTo: string; linkLabel: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h2>
        <Link to={linkTo} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
          {linkLabel} →
        </Link>
      </div>
      {children}
    </div>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    free: 'bg-slate-100 text-slate-600',
    pro: 'bg-indigo-100 text-indigo-700',
    premium: 'bg-violet-100 text-violet-700',
    enterprise: 'bg-amber-100 text-amber-700',
  };
  const cls = map[plan?.toLowerCase()] ?? 'bg-slate-100 text-slate-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${cls}`}>{plan || 'free'}</span>;
}

const CHART_H = 96; // px — bar area height
const Y_TICKS = [0, 0.25, 0.5, 0.75, 1];

function MiniBarChart({ data }: { data: { label: string; value: number }[] }) {
  const niceMax = _niceMax(Math.max(...data.map((d) => d.value), 1));

  // Format "YYYY-MM-DD" → "MM/DD"
  const xLabel = (raw: string) => {
    const p = raw.split('-');
    return p.length === 3 ? `${p[1]}/${p[2]}` : raw;
  };

  // Show every nth label so they don't crowd (target ≤7 visible)
  const step = Math.ceil(data.length / 7);

  return (
    <div className="flex gap-1 items-start">
      {/* Y-axis: label + ticks */}
      <div className="flex gap-0.5 items-start shrink-0">
        {/* Rotated axis title */}
        <div className="flex items-center justify-center shrink-0" style={{ width: 10, height: CHART_H }}>
          <span
            className="text-[8px] text-slate-400 whitespace-nowrap"
            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
          >
            Sessions
          </span>
        </div>
        {/* Tick values */}
        <div className="relative shrink-0" style={{ width: 20, height: CHART_H }}>
          {Y_TICKS.map((f) => (
            <span
              key={f}
              className="absolute right-0 text-[8px] text-slate-400 leading-none"
              style={{ bottom: `${f * 100}%`, transform: 'translateY(50%)' }}
            >
              {Math.round(niceMax * f)}
            </span>
          ))}
        </div>
      </div>

      {/* Chart body */}
      <div className="flex-1 flex flex-col gap-1">
        {/* Bar area */}
        <div
          className="relative border-l border-b border-slate-200"
          style={{ height: CHART_H }}
        >
          {/* Gridlines */}
          {Y_TICKS.filter((f) => f > 0).map((f) => (
            <div
              key={f}
              className="absolute left-0 right-0 border-t border-slate-100"
              style={{ bottom: `${f * 100}%` }}
            />
          ))}
          {/* Bars */}
          <div className="absolute inset-0 flex gap-0.5 px-1">
            {data.map((d) => (
              <div key={d.label} className="flex-1 h-full relative group">
                <div
                  className="absolute bottom-0 left-0 right-0 bg-indigo-400 rounded-t-sm opacity-80 group-hover:opacity-100 transition-opacity"
                  style={{ height: `${Math.max((d.value / niceMax) * 100, d.value > 0 ? 1.5 : 0)}%` }}
                />
                {/* Hover tooltip */}
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[9px] text-slate-700 bg-white border border-slate-200 px-1.5 py-0.5 rounded shadow-sm hidden group-hover:block whitespace-nowrap z-10">
                  {xLabel(d.label)}: {d.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* X-axis labels */}
        <div className="flex gap-0.5 px-1">
          {data.map((d, i) => (
            <div key={d.label} className="flex-1 text-center overflow-hidden">
              {(i % step === 0 || i === data.length - 1) && (
                <span className="text-[8px] text-slate-400 block leading-tight">{xLabel(d.label)}</span>
              )}
            </div>
          ))}
        </div>

        {/* X-axis title */}
        <p className="text-[8px] text-slate-400 text-center -mt-0.5">Date</p>
      </div>
    </div>
  );
}

function _niceMax(raw: number): number {
  if (raw <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  const rounded = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return rounded * mag;
}

const PROVIDER_DOT: Record<string, string> = {
  true: 'bg-emerald-400',
  false: 'bg-red-400',
};

// ---- Main Component ----

export default function Dashboard() {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [activity, setActivity] = useState<ActivityAnalytics | null>(null);
  const [revenue, setRevenue] = useState<RevenueAnalytics | null>(null);
  const [llmUsage, setLlmUsage] = useState<LLMUsage | null>(null);
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [recentUsers, setRecentUsers] = useState<UserListItem[]>([]);
  const [contentStats, setContentStats] = useState<ContentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      getAnalyticsSummary(),
      getActivityAnalytics('7d'),
      getRevenueAnalytics(),
      getLLMUsage(7),
      getLLMProviders(),
      adminApiClient.get('/users?page=1&page_size=5'),
      getContentStats(),
    ])
      .then(([s, act, rev, llm, prov, users, content]) => {
        setSummary(s.data);
        setActivity(act.data);
        setRevenue(rev.data);
        setLlmUsage(llm.data);
        setProviders(prov.data?.providers ?? []);
        setRecentUsers(users.data?.items ?? []);
        setContentStats(content.data);
      })
      .catch(() => setError('Failed to load dashboard data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (error) return <p className="text-red-500 text-sm">{error}</p>;

  // ---- Derived values ----
  const conversionPct =
    summary && summary.total_users > 0
      ? ((summary.paying_users / summary.total_users) * 100).toFixed(1)
      : '0.0';

  const retentionPct =
    summary && summary.total_users > 0
      ? ((summary.active_30d / summary.total_users) * 100).toFixed(1)
      : '0.0';

  const currentMonth = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const revenueThisMonth =
    revenue?.payment_timeline
      ?.filter((p) => p.date?.startsWith(currentMonth))
      .reduce((sum, p) => sum + (p.revenue ?? 0), 0) ?? 0;

  const topCurrency = revenue?.revenue_by_currency?.[0];

  const errorRate = llmUsage?.error_rate ?? 0;
  const errorRateColor = errorRate > 15 ? 'text-red-600 bg-red-50' : errorRate > 5 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600 bg-emerald-50';

  const unconfiguredProviders = providers.filter((p) => !p.configured);

  // Action items
  const alerts: { icon: string; text: string; linkTo: string; linkLabel: string; level: 'red' | 'amber' | 'ok' }[] = [];
  if ((summary?.pending_flags ?? 0) > 0)
    alerts.push({ icon: '🚩', text: `${summary!.pending_flags} content flag${summary!.pending_flags > 1 ? 's' : ''} pending review`, linkTo: '/content', linkLabel: 'Review', level: 'amber' });
  if ((contentStats?.patterns_pending_review ?? 0) > 0)
    alerts.push({ icon: '📋', text: `${contentStats!.patterns_pending_review} exam pattern${contentStats!.patterns_pending_review > 1 ? 's' : ''} pending approval`, linkTo: '/content', linkLabel: 'Review', level: 'amber' });
  if (errorRate > 15)
    alerts.push({ icon: '⚠️', text: `LLM error rate is ${errorRate.toFixed(1)}% in the last 7 days`, linkTo: '/llm-config', linkLabel: 'Inspect', level: 'red' });
  if (unconfiguredProviders.length > 0)
    alerts.push({ icon: '🔑', text: `${unconfiguredProviders.length} AI provider${unconfiguredProviders.length > 1 ? 's' : ''} missing API key`, linkTo: '/llm-config', linkLabel: 'Configure', level: 'red' });
  if ((summary?.paying_users ?? 0) === 0)
    alerts.push({ icon: '💳', text: 'No paying users yet — check subscription plans', linkTo: '/subscriptions', linkLabel: 'View', level: 'amber' });

  const alertBg: Record<string, string> = { red: 'bg-red-50 border-red-200 text-red-700', amber: 'bg-amber-50 border-amber-200 text-amber-700', ok: 'bg-emerald-50 border-emerald-200 text-emerald-700' };

  // Activity mini chart
  const activityChartData =
    activity?.daily_activity?.slice(-14).map((d) => ({
      label: d.date,
      value: (d.tutor ?? 0) + (d.exam ?? 0) + (d.practice ?? 0),
    })) ?? [];

  // Top plan
  const topPlan = revenue?.plan_distribution?.sort((a, b) => b.count - a.count)?.[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">Dashboard</h1>
        <span className="text-xs text-slate-400">Last 7 days unless noted</span>
      </div>

      {/* ── Section 1: KPI Row ─────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Total Users" value={summary?.total_users?.toLocaleString() ?? '—'} sub="all time" color="indigo" />
        <KpiCard label="Active (7d)" value={summary?.active_7d?.toLocaleString() ?? '—'} sub={`${retentionPct}% retention (30d)`} color="violet" />
        <KpiCard label="Paying Users" value={summary?.paying_users?.toLocaleString() ?? '—'} sub={`${conversionPct}% conversion`} color="emerald" />
        <KpiCard label="Sessions Today" value={summary?.sessions_today?.toLocaleString() ?? '—'} sub={`${summary?.sessions_this_week?.toLocaleString() ?? '—'} this week`} color="indigo" />
        <KpiCard label="New Signups" value={summary?.new_signups_today?.toLocaleString() ?? '—'} sub={`${summary?.new_signups_week?.toLocaleString() ?? '—'} this week`} color="violet" />
        <KpiCard label="Pending Flags" value={summary?.pending_flags?.toLocaleString() ?? '—'} sub="content reviews" color={(summary?.pending_flags ?? 0) > 0 ? 'amber' : 'slate'} />
      </div>

      {/* ── Section 2: Action Items ────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Action Items</h2>
        {alerts.length === 0 ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm">
            <span className="text-base">✅</span>
            <span>All clear — no issues requiring attention right now.</span>
          </div>
        ) : (
          <ul className="space-y-2">
            {alerts.map((a, i) => (
              <li key={i} className={`flex items-center gap-3 border rounded-lg px-4 py-2.5 text-sm ${alertBg[a.level]}`}>
                <span className="text-base shrink-0">{a.icon}</span>
                <span className="flex-1">{a.text}</span>
                <Link to={a.linkTo} className="text-xs font-semibold underline underline-offset-2 shrink-0">
                  {a.linkLabel}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Section 3 + 7: Activity & AI Health side by side ─ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Section 3: Activity Pulse (2/3 width) */}
        <SectionCard title="Activity Pulse — Last 14 Days" linkTo="/analytics" linkLabel="View Analytics" className="lg:col-span-2">
          {activityChartData.length > 0 ? (
            <>
              <MiniBarChart data={activityChartData} />
              <div className="mt-3 flex gap-4 text-xs text-slate-500">
                <span><span className="font-semibold text-slate-700">{activity?.feature_totals?.tutor?.toLocaleString() ?? 0}</span> tutor</span>
                <span><span className="font-semibold text-slate-700">{activity?.feature_totals?.exam?.toLocaleString() ?? 0}</span> exam</span>
                <span><span className="font-semibold text-slate-700">{activity?.feature_totals?.practice?.toLocaleString() ?? 0}</span> practice</span>
                <span><span className="font-semibold text-slate-700">{activity?.feature_totals?.notes?.toLocaleString() ?? 0}</span> notes</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Avg {activity?.avg_sessions_per_active_user?.toFixed(1) ?? '—'} sessions / active user
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-400">No activity data.</p>
          )}
        </SectionCard>

        {/* Section 7: AI Health (1/3 width) */}
        <SectionCard title="AI Health" linkTo="/llm-config" linkLabel="View LLM Config">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Error rate (7d)</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${errorRateColor}`}>
                {errorRate.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Total calls (7d)</span>
              <span className="text-xs font-semibold text-slate-700">{llmUsage?.total_calls?.toLocaleString() ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Avg latency</span>
              <span className="text-xs font-semibold text-slate-700">{llmUsage?.avg_duration_ms ? `${llmUsage.avg_duration_ms.toFixed(0)} ms` : '—'}</span>
            </div>
            <div className="border-t border-slate-100 pt-2">
              <p className="text-xs text-slate-400 mb-1.5">Provider status</p>
              <div className="space-y-1">
                {providers.length === 0 ? (
                  <p className="text-xs text-slate-400">No providers configured.</p>
                ) : (
                  providers.map((p) => (
                    <div key={p.name} className="flex items-center gap-2 text-xs">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${PROVIDER_DOT[String(p.configured)]}`} />
                      <span className="text-slate-600 capitalize">{p.name}</span>
                      {!p.configured && <span className="text-red-500 ml-auto">No key</span>}
                      {p.chain_usage_count > 0 && p.configured && (
                        <span className="text-slate-400 ml-auto">{p.chain_usage_count} chain{p.chain_usage_count > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* ── Sections 4, 5, 6: Three columns ─────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Section 5: Recent Users */}
        <SectionCard title="Recent Sign-ups" linkTo="/users" linkLabel="View Users">
          {recentUsers.length === 0 ? (
            <p className="text-sm text-slate-400">No users found.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {recentUsers.map((u) => (
                <li key={u.id} className="py-2 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0">
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{u.name || u.email}</p>
                    <p className="text-[10px] text-slate-400 truncate">{u.email}</p>
                  </div>
                  <PlanBadge plan={u.plan_id} />
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Section 4: Revenue Snapshot */}
        <SectionCard title="Revenue Snapshot" linkTo="/subscriptions" linkLabel="View Subscriptions">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Revenue this month</span>
              <span className="text-sm font-bold text-emerald-700">
                {topCurrency ? `${topCurrency.currency} ` : ''}{revenueThisMonth.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500">Paying users</span>
              <span className="text-sm font-semibold text-slate-700">{summary?.paying_users?.toLocaleString() ?? '—'}</span>
            </div>
            {topPlan && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Top plan</span>
                <div className="flex items-center gap-2">
                  <PlanBadge plan={topPlan.plan_id} />
                  <span className="text-xs text-slate-400">{topPlan.count} users</span>
                </div>
              </div>
            )}
            <div className="border-t border-slate-100 pt-2">
              <p className="text-xs text-slate-400 mb-1.5">Plan distribution</p>
              <div className="space-y-1">
                {(revenue?.plan_distribution ?? []).map((p) => (
                  <div key={p.plan_id} className="flex items-center gap-2 text-xs">
                    <PlanBadge plan={p.plan_id} />
                    <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                      <div
                        className="bg-indigo-400 h-1.5 rounded-full"
                        style={{
                          width: `${revenue!.plan_distribution.reduce((s, x) => s + x.count, 0) > 0
                            ? (p.count / revenue!.plan_distribution.reduce((s, x) => s + x.count, 0)) * 100
                            : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-slate-500 w-6 text-right">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Section 6: Content Health */}
        <SectionCard title="Content Health" linkTo="/content" linkLabel="View Content">
          {contentStats ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-slate-800">{contentStats.topics?.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Topics</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-slate-800">{contentStats.catalog_exams?.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Catalog Exams</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-slate-800">{contentStats.exam_patterns?.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Exam Patterns</p>
                </div>
                <div className={`rounded-lg p-2.5 text-center ${contentStats.patterns_pending_review > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                  <p className={`text-lg font-bold ${contentStats.patterns_pending_review > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
                    {contentStats.patterns_pending_review?.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Pending Review</p>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-2 flex justify-between text-xs text-slate-500">
                <span><span className="font-semibold text-slate-700">{contentStats.exam_sessions?.toLocaleString()}</span> exam sessions</span>
                <span><span className="font-semibold text-slate-700">{contentStats.practice_sessions?.toLocaleString()}</span> practice</span>
              </div>
              {contentStats.pending_flags > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <span>🚩</span>
                  <span>{contentStats.pending_flags} content flag{contentStats.pending_flags > 1 ? 's' : ''} need review</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No content data.</p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
